import { inject, Injectable } from '@angular/core';
import {
  AgroclimaticZoneService,
  Crop,
  FieldFilter,
  FieldTypeService,
  SoilTypeService,
} from '@6grain/masofa-monolith-api-client/dictionaries';
import {
  CustomFieldViewModel,
  Field,
  FieldService,
  FieldWithAreaViewModel,
  Season,
  SeasonService,
} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import { signal } from '@angular/core';
import { CropService } from '@6grain/masofa-monolith-api-client/dictionaries/api/crop.service';
import { Observable, of, Subject } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { IMsfFormBuilder } from '@components/form-builder/form-builder';
import { RegionManagerService } from '@components/openstreetmap/managers/region-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { IFieldSources } from '@models/IFieldSource';

type FieldWithEntity = { entity: Field };

@Injectable({
  providedIn: 'root',
})
export class PassportManagerService {
  protected datePipe!: DatePipe;

  protected readonly seasonService: SeasonService = inject(SeasonService);
  protected readonly cropService: CropService = inject(CropService);
  protected readonly fieldService: FieldService = inject(FieldService);
  protected readonly regionManagerService: RegionManagerService = inject(RegionManagerService);
  protected readonly soilTypeService: SoilTypeService = inject(SoilTypeService);
  protected readonly agroClimaticService: AgroclimaticZoneService = inject(AgroclimaticZoneService);
  protected readonly fieldTypeService: FieldTypeService = inject(FieldTypeService);
  protected readonly translateService: TranslateService = inject(TranslateService);

  public fieldActiveSeasons = signal<Season[]>([]);
  public fieldActiveCrops = signal<Crop[]>([]);
  public crops = signal<Crop[]>([]);
  public field = signal<FieldWithEntity | null>(null);
  public customField = signal<CustomFieldViewModel | null>(null);
  public isLoadingActiveSeasons = signal(false);
  public isLoadingCrops = signal(false);
  public isLoadingField = signal(false);
  public isLoadingCustomField = signal(false);

  afterLoadField: Subject<Field> = new Subject<Field>();

  public usedArea = signal<number>(0);
  public freeArea = signal<number>(0);
  public totalArea = signal<number>(0);

  initialize(datePipe: DatePipe) {
    this.datePipe = datePipe;
  }

  setFieldActiveSeason(seasons: Season[]) {
    this.fieldActiveSeasons.set(seasons);
    this.setActiveCrops();
  }

  getFieldActiveSeason(): Season[] {
    return this.fieldActiveSeasons();
  }

  addFieldActiveSeason(season: Season) {
    this.fieldActiveSeasons.update((seasons) => [...seasons, season]);
  }

  setCrops(crops: Crop[]) {
    this.crops.set(crops);
    this.setActiveCrops();
  }

  setCustomField(customField: CustomFieldViewModel) {
    this.customField.set(customField);
  }

  getFieldCrops(): Crop[] {
    return this.crops();
  }

  setField(field: FieldWithEntity) {
    this.field.set(field as FieldWithEntity);
  }

  getField(): FieldWithEntity | null {
    return this.field();
  }

  setActiveCrops() {
    const activeCrops: Crop[] = [];
    this.fieldActiveSeasons().forEach((season) => {
      if (season.cropId) {
        const crop = this.crops().find((c) => c.id === season.cropId);
        if (crop) {
          activeCrops.push(crop);
        }
      }
    });
    this.fieldActiveCrops.set(activeCrops);
  }





  /**
   * Active seasons for a field at the given date (defaults to "now" server-side when omitted).
   * Backed by `/cropMonitoring/Season/GetActiveSeasonByDate`; the previous client-side
   * `PlantingDate <= today` filtering is no longer performed here.
   */
  loadActiveSeasons(fieldId: string, date?: string): Observable<Season[]> {
    this.isLoadingActiveSeasons.set(true);
    return this.seasonService
      .cropMonitoringSeasonGetActiveSeasonByDateGet(fieldId, date)
      .pipe(
        tap((seasons) => {
          this.setFieldActiveSeason(seasons as Season[]);
          this.isLoadingActiveSeasons.set(false);
        }),
        catchError(() => {
          this.setFieldActiveSeason([]);
          this.isLoadingActiveSeasons.set(false);
          return of([] as Season[]);
        }),
      );
  }

  loadCrops(): Observable<Crop[]> {
    this.isLoadingCrops.set(true);
    return this.cropService.dictionariesCropGetByQueryPost({}).pipe(
      tap((crops) => {
        this.setCrops(crops as Crop[]);
        this.isLoadingCrops.set(false);
      }),
      catchError(() => {
        this.setCrops([]);
        this.isLoadingCrops.set(false);
        return of([] as Crop[]);
      }),
    );
  }

  loadField(fieldId: string): Observable<FieldWithAreaViewModel | null> {
    this.isLoadingField.set(true);
    return this.fieldService.cropMonitoringFieldGetByIdWithAreaIdGet(fieldId).pipe(
      tap((viewModel: FieldWithAreaViewModel) => {
        if (viewModel.field) {
          this.setField({ entity: viewModel.field });
          this.totalArea.set(viewModel.totalArea as number);
          this.usedArea.set(viewModel.usedArea as number);
          this.afterLoadField.next(viewModel.field);
        }
        this.isLoadingField.set(false);
      }),
      catchError(() => {
        this.setField(null as any);
        this.usedArea.set(0);
        this.freeArea.set(0);
        this.totalArea.set(0);
        this.isLoadingField.set(false);
        return of(null);
      }),
    );
  }

  loadFieldCustomData(fieldId: string): Observable<CustomFieldViewModel | null> {
    this.isLoadingCustomField.set(true);
    const filters: FieldFilter[] = [
      {
        filterField: 'id',
        filterOperator: 0,
        filterValue: fieldId,
      },
    ];
    const params = {
      filters: filters,
    };
    return this.fieldService.cropMonitoringFieldCustomGetByQueryPost(params).pipe(
      map((fields) => {
        if (fields && fields.length > 0) {
          const field = fields[0];
          this.setCustomField(field as CustomFieldViewModel);
          this.isLoadingCustomField.set(false);
          return field as CustomFieldViewModel;
        } else {
          this.setCustomField(null as any);
          this.isLoadingCustomField.set(false);
          return null;
        }
      }),
      catchError(() => {
        this.setCustomField(null as any);
        this.isLoadingCustomField.set(false);
        return of(null);
      }),
    );
  }

  getEditFieldFormConfig(): IMsfFormBuilder[] {
    return [
      {
        name: 'name',
        title: 'Name',
        type: 'input',
        placeholder: 'Enter',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'bonitetScore',
        title: 'Bonitet score',
        type: 'input',
        placeholder: '1-100',
        options: {
          col: 'col-6',
        },
      },
      // {
      //   name: 'regionId',
      //   title: 'Region',
      //   type: 'select',
      //   selectService: this.regionManagerService,
      //   selectServiceMethod: "getRegionsObservable",
      //   placeholder: 'Select'
      // }, dictionariesRegionGetByQueryPost
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagerService,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          col: 'col-6',

          treeSelect: {
            treeBy: 'parentId',
          },
        },
        placeholder: 'Select region',
      },
      {
        name: 'soilTypeId',
        title: 'Soil type',
        type: 'select',
        selectService: this.soilTypeService,
        selectServiceMethod: 'dictionariesSoilTypeGetByQueryPost',
        placeholder: 'Select',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'openClosedSoil',
        title: 'Soil Context',
        type: 'select',
        placeholder: 'Select',
        selectOptions: [
          { id: 1, name: this.translateService.instant('Open Soil') },
          { id: 2, name: this.translateService.instant('Closed Soil') },
        ],
        options: {
          col: 'col-6',
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            translateBy: 'none',
          },
        },

      },
      {
        name: 'fieldTypeId',
        title: 'Field Type',
        type: 'select',
        selectService: this.fieldTypeService,
        selectServiceMethod: 'dictionariesFieldTypeGetByQueryPost',
        placeholder: 'Select field type',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'agroclimaticZoneId',
        title: 'Agroclimatic zone',
        type: 'select',
        selectService: this.agroClimaticService,
        selectServiceMethod: 'dictionariesAgroclimaticZoneGetByQueryPost',
        placeholder: 'desert',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'classifier',
        title: 'Classifier',
        type: 'input',
        placeholder: 'Classifier',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'source',
        title: 'Source',
        type: 'select',
        placeholder: 'Select source',
        selectOptions: IFieldSources,
        options: {
          clearable: false,
          select: { optionLabel: 'name', translateBy: 'locale' },
          col: 'col-6',
        },
      },
      {
        name: 'contourConfirmationDate',
        title: 'Date of contour confirmation',
        placeholder: 'Date of contour confirmation',
        type: 'date',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'cadastralNumber',
        title: 'CADASTRE_NUMBER',
        placeholder: 'CADASTRE_NUMBER',
        type: 'input',
        options: {
          col: 'col-6',
        },
      },
      {
        name: 'comment',
        title: 'Comment',
        type: 'textarea',
        placeholder: 'Comment',
        options: {
          col: 'col-6',
        },
      },
    ];
  }
}
